/**
 * Turning the MFA setup QR code into a PNG the browser can hand to the
 * clipboard.
 *
 * `react-qr-code` draws an inline `<svg>`, and neither half of what a user
 * wants here works on one: browsers only offer "Copy image" on `<img>` and
 * `<canvas>`, and `ClipboardItem` has no image/svg+xml flavour any engine
 * accepts. Rasterising once, up front, buys both — the `<img>` that gets
 * displayed and the blob the copy button writes.
 */

/** Intrinsic size react-qr-code draws the SVG at, in design px. */
export const QR_SVG_SIZE = 160;

/**
 * Multiplier applied when rasterising. The PNG leaves at 3x so it stays
 * scannable after whatever it is pasted into scales it back up.
 */
export const QR_EXPORT_SCALE = 3;

/** Edge length of the exported PNG, in real pixels. */
export const QR_EXPORT_PIXELS = QR_SVG_SIZE * QR_EXPORT_SCALE;

/** Painted under the code so a transparent PNG can never reach a dark surface. */
const QR_BACKGROUND = '#ffffff';

const COPY_BY_HAND_HINT =
  'You can right-click the QR code and choose "Copy image" instead.';

export interface QrCodePng {
  readonly blob: Blob;
  readonly dataUrl: string;
}

export const serializeSvg = (svg: SVGSVGElement): string =>
  new XMLSerializer().serializeToString(svg);

/**
 * Percent-encoded rather than base64 — `btoa` throws on any character outside
 * Latin-1, and the account label baked into the QR payload can hold one.
 */
export const svgMarkupToDataUrl = (markup: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;

/**
 * Draws SVG markup into a canvas and reads it back as a PNG, returning both
 * shapes the UI needs: a data URL to display and a blob to copy.
 */
export const svgMarkupToPng = (
  markup: string,
  pixels: number
): Promise<QrCodePng> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixels;
      canvas.height = pixels;

      const context = canvas.getContext('2d');

      if (context == null) {
        reject(new Error('Could not get a 2d canvas context.'));
        return;
      }

      context.fillStyle = QR_BACKGROUND;
      context.fillRect(0, 0, pixels, pixels);
      context.drawImage(image, 0, 0, pixels, pixels);

      canvas.toBlob((blob) => {
        if (blob == null) {
          reject(new Error('Could not read the QR code back as a PNG.'));
          return;
        }

        resolve({ blob, dataUrl: canvas.toDataURL('image/png') });
      }, 'image/png');
    };

    image.onerror = () =>
      reject(new Error('Could not render the QR code SVG.'));

    image.src = svgMarkupToDataUrl(markup);
  });

/**
 * Null when this browser can put an image on the clipboard, otherwise the
 * reason it cannot, phrased for the user. The check returns its own message so
 * the two cannot drift apart.
 */
export const getImageClipboardBlocker = (): string | null => {
  if (!window.isSecureContext) {
    return `Copying an image needs a secure (https) connection. ${COPY_BY_HAND_HINT}`;
  }

  if (
    !('ClipboardItem' in window) ||
    typeof navigator.clipboard?.write !== 'function'
  ) {
    return `This browser cannot copy images to the clipboard. ${COPY_BY_HAND_HINT}`;
  }

  return null;
};

export const writeImageToClipboard = async (blob: Blob): Promise<void> => {
  const blocker = getImageClipboardBlocker();

  if (blocker !== null) {
    throw new Error(blocker);
  }

  await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
};
